import "./subway.scss";

import { isNil } from "lodash";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";

import { withContainerContext } from "../../components/container/ContainerContext";
import { withScreenContext } from "../../components/screen/ScreenContext";
import { withScrollPositionContext } from "../../components/scrollable/ScrollPositionContext";
import {
  SUBWAY_VIEW_THREAD_LABEL_HEIGHT,
  SUBWAY_VIEW_THREAD_LABEL_LEFT,
  SUBWAY_VIEW_THREAD_LABEL_WIDTH
} from "../../constants";
import { scalePixelsToFontSize } from "../../constants/styles/dimensions";
import { immutableRecords, PropTypeShapes } from "../../types";
import SubwayThreadLabelView from "./SubwayThreadLabelView";

class SubwayThreadColumnView extends PureComponent {
  constructor(props, context) {
    super(props, context);

    this.state = {
      highlightDropThread: null
    };

    this._horizontalScrollerActive = false;
    this._verticalScrollerActive = false;

    this.applyStyles = this.applyStyles.bind(this);
    this.getStyles = this.getStyles.bind(this);

    this.getRenderedThreads = this.getRenderedThreads.bind(this);

    this._liveScrollListeners = {
      onLiveScrollStart: this.onLiveScrollStart.bind(this),
      onLiveScrollUpdate: this.onLiveScrollUpdate.bind(this),
      onLiveScrollStop: this.onLiveScrollStop.bind(this)
    };
  }

  componentDidMount() {
    this.applyStyles();
    this.props.xScrollActions.registerForLiveUpdates(this._liveScrollListeners);
    this.props.yScrollActions.registerForLiveUpdates(this._liveScrollListeners);
  }

  componentDidUpdate() {
    this.applyStyles();
  }

  componentWillUnmount() {
    this.props.xScrollActions.unregisterForLiveUpdates(this._liveScrollListeners);
    this.props.yScrollActions.unregisterForLiveUpdates(this._liveScrollListeners);
  }

  onLiveScrollStart(horizontalScroller, verticalScroller) {
    if (horizontalScroller) {
      this._horizontalScrollerActive = true;
    }
    if (verticalScroller) {
      this._verticalScrollerActive = true;
    }
    this.applyStyles();
  }

  onLiveScrollUpdate(scrollLeft, scrollTop) {
    // Update both liveTop and liveLeft as needed, but we only need to applyTransform when
    // we are changing top.
    if (!isNil(scrollLeft) && scrollLeft !== this._liveLeft) {
      this._liveLeft = scrollLeft;
    }
    if (!isNil(scrollTop) && scrollTop !== this._liveTop) {
      this._liveTop = scrollTop;
      this.applyStyles();
    }
  }

  onLiveScrollStop(horizontalScroller, verticalScroller) {
    if (horizontalScroller) {
      this._horizontalScrollerActive = false;
    }
    if (verticalScroller) {
      this._verticalScrollerActive = false;
    }
    this.applyStyles();
  }

  applyStyles() {
    const styles = this.getStyles();
    this._domNode.style.left = styles.nodeStyle.left;
    this._domNode.style.top = styles.nodeStyle.top;
    this._domNode.style.height = styles.nodeStyle.height;
    this._domNode.style.overflow = styles.nodeStyle.overflow;
    this._domNode.style.position = styles.nodeStyle.position;
    this._transformNode.style.transform = styles.transformStyle.transform;
  }

  getStyles() {
    // Node styles
    let left = `${this._liveLeft}px`;
    let top = "0px";
    let height = `${this.props.yScrollPosition.viewport.max}px`;
    let overflow = null;
    let position = "absolute";
    const maxWidth = `${this.props.containerRect.width}px`;

    // Transform style
    let transform = null;

    if (this._horizontalScrollerActive) {
      left = `${this.props.containerRect.left}px`;
      top = `${this.props.containerRect.top}px`;
      height = `${this.props.containerRect.height}px`;
      position = "fixed";
      overflow = "hidden";
      transform = `translateY(${-this._liveTop}px)`;
    }

    const nodeStyle = {
      left,
      top,
      height,
      overflow,
      position,
      maxWidth
    };
    const transformStyle = {
      transform
    };

    return {
      nodeStyle,
      transformStyle
    };
  }

  getViewport() {
    const result = immutableRecords.FlatRectRecord({
      left: this.props.xScrollPosition.viewport.min,
      right: this.props.xScrollPosition.viewport.max,
      width: this.props.xScrollPosition.viewport.range,
      top: this.props.yScrollPosition.viewport.min,
      bottom: this.props.yScrollPosition.viewport.max,
      height: this.props.yScrollPosition.viewport.range
    });
    return result;
  }

  getRenderedThreads(data, viewport) {
    // Cut down what we are rendering to just the nodes and parts of threads within
    // the viewport

    // for each thread, we need to keep any vertices within the X range, unless we want
    // to do further calculations on the y range (since lines may be diagonal etc.)
    // we also need to include the node immediately before and after the range
    const threads = data.threads.filter(t => {
      return t.anchorY >= viewport.top && t.anchorY <= viewport.bottom;
    });

    return data.merge({
      threads
    });
  }

  renderThreadLabels(threads, allowDrag) {
    const SCALED_THREAD_LABEL_LEFT = scalePixelsToFontSize(SUBWAY_VIEW_THREAD_LABEL_LEFT, this.props.fontSize);
    const SCALED_THREAD_LABEL_WIDTH = scalePixelsToFontSize(SUBWAY_VIEW_THREAD_LABEL_WIDTH, this.props.fontSize);
    // const SCALED_THREAD_LABEL_MARGIN = scalePixelsToFontSize(SUBWAY_VIEW_THREAD_LABEL_MARGIN, this.props.fontSize);
    const SCALED_THREAD_LABEL_HEIGHT = scalePixelsToFontSize(SUBWAY_VIEW_THREAD_LABEL_HEIGHT, this.props.fontSize);

    return threads.map(t => {
      const left = SCALED_THREAD_LABEL_LEFT;
      const top = t.anchorY - SCALED_THREAD_LABEL_HEIGHT / 2;
      return (
        <SubwayThreadLabelView
          key={t.id}
          viewId={this.props.viewId}
          onClick={this.props.onItemClick}
          onDoubleClick={this.props.onItemDoubleClick}
          id={t.id}
          itemId={t.id}
          selected={this.props.selectedItemIds.indexOf(t.id) >= 0}
          color={t.color}
          relativeLeft={left}
          relativeTop={top}
          width={SCALED_THREAD_LABEL_WIDTH}
          height={SCALED_THREAD_LABEL_HEIGHT}
          typeId={t.typeId}
          typeIcon={t.typeIcon}
          typeIconColor={t.typeIconColor}
          typeLabel={t.typeLabel}
          typeLabelDisplay={t.typeLabelDisplay}
          initials={t.initials}
          label={t.label}
          allowDrag={allowDrag}
          onLabelChange={this.props.onLabelChange}
          autoEditWhenBlank={this.props.autoEditBlankNodes}
          isActiveView={this.props.isActiveView}
        />
      );
    });
  }

  renderDropThread(threads, threadId) {
    const thread = threads.find(t => t.id === threadId);
    const style = {
      left: 0,
      top: thread.top,
      width: "100%",
      height: thread.bottom
    };
    return <div className="subwayThreadDrop" style={style} />;
  }

  render() {
    const data = this.props.data;
    const styles = this.getStyles();
    const viewport = this.getViewport();

    let allowDrag = true;
    //#!if iOS === true
// allowDrag = this.props.screenContext.isEditMode;
    //#!endif

    const renderedData = this.getRenderedThreads(data, viewport);

    return (
      <div className="subwayTrackCanvasView" style={styles.nodeStyle} ref={node => (this._domNode = node)}>
        <div className="subwayTrackTransformView" ref={node => (this._transformNode = node)}>
          {this.state.highlightDropThread ? this.renderDropThread(data.threads, this.state.highlightDropThread) : null}
          {this.renderThreadLabels(renderedData.threads, allowDrag)}
        </div>
      </div>
    );
  }
}

SubwayThreadColumnView.propTypes = {
  fontSize: PropTypes.number.isRequired,
  viewId: PropTypes.string.isRequired,
  data: PropTypeShapes.subwayData.isRequired,
  allowedThreads: PropTypeShapes.listOptionGroupList.isRequired,
  visibleItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  selectedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  onNodeDrop: PropTypes.func.isRequired,
  // onInternalDragLeave: PropTypes.func.isRequired,
  // onEventDrop: PropTypes.func.isRequired,
  // getConnectorDropOptions: PropTypes.func.isRequired,
  // onRelationshipChange: PropTypes.func.isRequired,
  onThreadToggle: PropTypes.func.isRequired,
  onAddThread: PropTypes.func.isRequired,
  onAddNode: PropTypes.func.isRequired,
  onItemClick: PropTypes.func.isRequired,
  onItemDoubleClick: PropTypes.func.isRequired,
  subwayViewSettings: PropTypeShapes.subwayViewSettings.isRequired,

  requestCanDropItems: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func.isRequired,
  isActiveView: PropTypes.bool.isRequired,
  autoEditBlankNodes: PropTypes.bool.isRequired,

  // Also accessed by ScrollableView
  requestViewBoundsForZoom: PropTypes.func.isRequired,

  // From withScrollPositionContext
  xScrollPosition: PropTypes.instanceOf(immutableRecords.ScrollPositionRecord).isRequired,
  xScrollActions: PropTypeShapes.scrollActions.isRequired,
  yScrollPosition: PropTypes.instanceOf(immutableRecords.ScrollPositionRecord).isRequired,
  yScrollActions: PropTypeShapes.scrollActions.isRequired,

  // From containerContext
  containerRect: PropTypeShapes.flatRect.isRequired,

  //#!if iOS === true
  // From withScreenContext
// screenContext: PropTypes.object
  //#!endif
};

const normalView = withScrollPositionContext(withContainerContext(SubwayThreadColumnView));
let exportedView = normalView;
//#!if iOS === true
// exportedView = withScreenContext(normalView);
//#!endif

export default exportedView;
